import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { appEvents } from "@/shared/lib/events";
import type { SubAgentId } from "@/features/ads";
import { ChatListRow } from "./ChatListRow";
import { useChatListRows, type ChatListRowModel } from "./hooks/useChatListRows";

const AGENT_ROW_TARGET_PREFIX = "companion-agent-row-";
const AGENT_LIST_TARGET = "companion-agent-list";
const AGENT_LIST_TEAM_SWEEP_TARGET = "companion-agent-list-team-sweep";
const AGENT_LIST_SWEEP_STAGGER_MS = 160;
const AGENT_LIST_TOOLTIP_PLACEMENT = "outside-middle";

export interface ChatListViewProps {
  isLoading?: boolean;
  onStop?: () => void;
  showcaseAllAgents?: boolean;
  showcaseFocusAgentId?: string | null;
  dataHighlightId?: string;
  onSelectMarcus?: () => void;
  onSelectSubAgent?: (agentId: SubAgentId) => void;
}

function renderChatListRow(
  { rowKey, ...row }: ChatListRowModel,
  sweepGeneration: number,
  sweepDelayMs: number,
) {
  return (
    <ChatListRow
      key={rowKey}
      {...row}
      sweepGeneration={sweepGeneration}
      sweepDelayMs={sweepDelayMs}
    />
  );
}

export function ChatListView({
  isLoading,
  onStop,
  showcaseAllAgents = false,
  showcaseFocusAgentId = null,
  dataHighlightId,
  onSelectMarcus,
  onSelectSubAgent,
}: ChatListViewProps) {
  const onboardingShowcase = useOnboardingAgentShowcase();
  const sweepGeneration = useAgentListTeamSweep();
  const effectiveShowcaseAllAgents = showcaseAllAgents || onboardingShowcase.active;
  const effectiveShowcaseFocusAgentId = showcaseFocusAgentId ?? onboardingShowcase.focusAgentId;
  const { rows, hasSubAgentRows } = useChatListRows({
    isLoading,
    onStop,
    showcaseAllAgents: effectiveShowcaseAllAgents,
    showcaseFocusAgentId: effectiveShowcaseFocusAgentId,
    onSelectMarcus,
    onSelectSubAgent,
  });
  const [marcusRow, ...subAgentRows] = rows;
  const highlightId =
    dataHighlightId ?? (effectiveShowcaseAllAgents ? AGENT_LIST_TARGET : undefined);
  const useAgentListTooltipPlacement = highlightId === AGENT_LIST_TARGET;

  return (
    <motion.div
      className={cn(
        "h-full flex flex-col relative",
        effectiveShowcaseAllAgents && "bg-card/35 backdrop-blur-xl",
      )}
      data-highlight-id={highlightId}
      data-highlight-tooltip-placement={
        useAgentListTooltipPlacement ? AGENT_LIST_TOOLTIP_PLACEMENT : undefined
      }
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 360, damping: 32 }}
    >
      {effectiveShowcaseAllAgents && (
        <div
          aria-hidden
          data-highlight-id={AGENT_LIST_TEAM_SWEEP_TARGET}
          data-highlight-tooltip-placement={AGENT_LIST_TOOLTIP_PLACEMENT}
          className="pointer-events-none absolute inset-0"
        />
      )}
      {/* Conversation list */}
      <motion.div
        layout
        className="flex-1 overflow-y-auto ds-scrollbar"
        transition={{ type: "spring", stiffness: 360, damping: 32 }}
      >
        {marcusRow ? renderChatListRow(marcusRow, sweepGeneration, 0) : null}

        {/* Divider if sub-agents exist */}
        {hasSubAgentRows && <div className="mx-4 border-t border-border/10" />}

        {subAgentRows.map((row, index) =>
          renderChatListRow(row, sweepGeneration, (index + 1) * AGENT_LIST_SWEEP_STAGGER_MS),
        )}
      </motion.div>
    </motion.div>
  );
}

function agentIdFromShowcaseTarget(target: string): string | null {
  if (!target.startsWith(AGENT_ROW_TARGET_PREFIX)) return null;
  return target.slice(AGENT_ROW_TARGET_PREFIX.length);
}

function useOnboardingAgentShowcase(): {
  active: boolean;
  focusAgentId: string | null;
} {
  const [showcase, setShowcase] = useState({
    active: false,
    focusAgentId: null as string | null,
  });

  useEffect(() => {
    const unsubscribeShowcase = appEvents.subscribe(
      "marcus-onboarding:agent-list-showcase",
      (event) => {
        setShowcase({
          active: event.active,
          focusAgentId: null,
        });
      },
    );
    const unsubscribeCursor = appEvents.subscribe("marcus-cursor-move-to", (event) => {
      const focusAgentId = agentIdFromShowcaseTarget(event.target);
      if (!focusAgentId) {
        if (
          event.target === AGENT_LIST_TARGET ||
          event.target === AGENT_LIST_TEAM_SWEEP_TARGET
        ) {
          setShowcase((current) => (current.active ? { ...current, focusAgentId: null } : current));
        }
        return;
      }
      setShowcase((current) => (current.active ? { ...current, focusAgentId } : current));
    });
    return () => {
      unsubscribeShowcase();
      unsubscribeCursor();
    };
  }, []);

  return showcase;
}

/**
 * Counter that bumps every time Marcus's onboarding emits the team-sweep
 * event. ChatListRow uses this as a framer-motion `key` to (re)play the
 * piano-key activation animation across the agent list.
 */
function useAgentListTeamSweep(): number {
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    return appEvents.subscribe("marcus-onboarding:agent-list-sweep", () => {
      setGeneration((current) => current + 1);
    });
  }, []);
  return generation;
}
